import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { FormTemplate, SubmitFormResponsePayload } from '../interfaces/forms';
import { submitFormResponse } from '../../../api/formsApi';
import { fetchCarsNoPagination } from '../../../api/carsApi';
import { CarResponse } from '../../../responses/CarResponse';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { notify } from '../../../shared/feedback/notify';
import '../formularios.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

type CarOption = Pick<CarResponse, 'id' | 'plate' | 'model'>;

interface FillFormModalProps {
  isOpen: boolean;
  template: FormTemplate | null;
  onClose: () => void;
  onSubmitted: () => void;
  preselectedCar?: CarOption | null;
}

const FillFormModal: React.FC<FillFormModalProps> = ({ isOpen, template, onClose, onSubmitted, preselectedCar }) => {
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [carSearch, setCarSearch] = useState('');
  const [cars, setCars] = useState<CarResponse[]>([]);
  const [selectedCar, setSelectedCar] = useState<CarOption | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && template) {
      setAnswers({});
      setCarSearch('');
      setCars([]);

      if (preselectedCar) {
        setSelectedCar(preselectedCar);
        return;
      }

      setSelectedCar(null);

      if (template.requiresCar) {
        fetchCarsNoPagination()
          .then((data) => setCars(data || []))
          .catch((error) => {
            // Sem o aviso, o select de veículo fica vazio como se não houvesse veículo algum.
            setCars([]);
            notify.errorFrom(
              error,
              'Não foi possível carregar a lista de veículos. Recarregue a página; se persistir, acione a TI.'
            );
          });
      }
    }
  }, [isOpen, template, preselectedCar]);

  if (!isOpen || !template) return null;

  const filteredCars = carSearch.trim()
    ? cars.filter((car) =>
        `${car.plate} ${car.model}`.toLowerCase().includes(carSearch.trim().toLowerCase())
      )
    : [];

  const handleTextChange = (fieldId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSingleChoiceChange = (fieldId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleMultipleChoiceChange = (fieldId: string, option: string, checked: boolean) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[fieldId]) ? (prev[fieldId] as string[]) : [];
      const next = checked ? [...current, option] : current.filter((item) => item !== option);
      return { ...prev, [fieldId]: next };
    });
  };

  const handleScaleChange = (fieldId: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: String(value) }));
  };

  const handleSubmit = async () => {
    if (template.requiresCar && !selectedCar) {
      toast.warning('Selecione um veículo para continuar.');
      return;
    }

    for (const field of template.fields) {
      if (!field.required) continue;
      const value = answers[field.id || ''];
      if (
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        toast.warning(`O campo "${field.label}" é obrigatório.`);
        return;
      }
    }

    // at-67 (BUG-V01): campo sem resposta não é enviado.
    //
    // Antes o payload levava TODOS os campos, com valor vazio nos não respondidos. No
    // backend, o vazio de um campo de escala virava **zero** (`Number('') === 0`) e batia
    // no mínimo da escala: um campo **opcional** deixado em branco recusava o envio do
    // formulário inteiro, citando um campo que o usuário não precisava responder. O
    // backend ganhou a defesa na mesma entrega; aqui a correção é não afirmar resposta
    // onde não houve — campo ausente é exatamente o que "não respondi" significa.
    const respondeu = (valor: string | string[] | undefined) => {
      if (valor === undefined || valor === null) return false;
      if (Array.isArray(valor)) return valor.length > 0;
      return valor !== '';
    };

    const payload: SubmitFormResponsePayload = {
      carId: selectedCar?.id,
      answers: template.fields
        .filter((field) => respondeu(answers[field.id || '']))
        .map((field) => ({
          fieldId: field.id || '',
          value: answers[field.id || ''],
        })),
    };

    setSubmitting(true);
    try {
      await submitFormResponse(template.id, payload);
      toast.success('Formulário enviado com sucesso.');
      onSubmitted();
    } catch (err) {
      // at-64 (BUG-V25): `err.message` de um erro do axios é "Request failed with status
      // code 400" — a explicação do backend (qual campo, qual intervalo) fica no corpo da
      // resposta e nunca chegava à tela. `actionError` extrai o `message` do backend.
      notify.actionError(err, { action: 'send', subject: 'o formulário' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {submitting && <FullScreenLoader />}
      <div className="formularios-modal-overlay" onClick={onClose}>
        <div className="formularios-modal" onClick={(e) => e.stopPropagation()}>
          <div className="formularios-modal-header">
            <h2 className="formularios-modal-title">{template.title}</h2>
            <button className="close-button" type="button" onClick={onClose}>&times;</button>
          </div>

          <div className="formularios-modal-body">
            {template.description && (
              <p className="formularios-modal-description">{template.description}</p>
            )}

            {template.requiresCar && (
              <div className="formularios-fill-field">
                <div className="formularios-fill-label">
                  Veículo<span className="formularios-fill-required">*</span>
                </div>
                {preselectedCar ? (
                  <div className="formularios-car-selected">
                    <span>{preselectedCar.plate} - {preselectedCar.model}</span>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      className="formularios-input"
                      placeholder="Buscar por placa ou modelo"
                      value={carSearch}
                      onChange={(e) => {
                        setCarSearch(e.target.value);
                        setSelectedCar(null);
                      }}
                    />
                    {filteredCars.length > 0 && !selectedCar && (
                      <div className="formularios-car-results">
                        {filteredCars.slice(0, 20).map((car) => (
                          <div
                            key={car.id}
                            className="formularios-car-result-item"
                            onClick={() => {
                              setSelectedCar(car);
                              setCarSearch('');
                            }}
                          >
                            {car.plate} - {car.model}
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedCar && (
                      <div className="formularios-car-selected">
                        <span>{selectedCar.plate} - {selectedCar.model}</span>
                        <button
                          type="button"
                          className="formularios-icon-btn"
                          onClick={() => setSelectedCar(null)}
                        >
                          Remover
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {template.fields.map((field) => (
              <div className="formularios-fill-field" key={field.id}>
                <div className="formularios-fill-label">
                  {field.label}
                  {field.required && <span className="formularios-fill-required">*</span>}
                </div>

                {field.type === 'TEXT_SHORT' && (
                  <input
                    type="text"
                    className="formularios-input"
                    value={(answers[field.id || ''] as string) || ''}
                    onChange={(e) => handleTextChange(field.id || '', e.target.value)}
                  />
                )}

                {field.type === 'TEXT_LONG' && (
                  <textarea
                    className="formularios-textarea"
                    value={(answers[field.id || ''] as string) || ''}
                    onChange={(e) => handleTextChange(field.id || '', e.target.value)}
                  />
                )}

                {field.type === 'SINGLE_CHOICE' && (
                  <div className="formularios-radio-group">
                    {(field.options || []).map((option) => (
                      <label key={option} className="formularios-checkbox-item">
                        <input
                          type="radio"
                          name={`field-${field.id}`}
                          checked={answers[field.id || ''] === option}
                          onChange={() => handleSingleChoiceChange(field.id || '', option)}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                )}

                {field.type === 'MULTIPLE_CHOICE' && (
                  <div className="formularios-checkbox-group-vertical">
                    {(field.options || []).map((option) => {
                      const current = Array.isArray(answers[field.id || ''])
                        ? (answers[field.id || ''] as string[])
                        : [];
                      return (
                        <label key={option} className="formularios-checkbox-item">
                          <input
                            type="checkbox"
                            checked={current.includes(option)}
                            onChange={(e) => handleMultipleChoiceChange(field.id || '', option, e.target.checked)}
                          />
                          {option}
                        </label>
                      );
                    })}
                  </div>
                )}

                {field.type === 'SCALE' && (
                  <div className="formularios-scale-container" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: 'fit-content', maxWidth: '100%' }}>
                    <div className="formularios-scale-group" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '4px 0' }}>
                      {Array.from(
                        { length: (field.scaleMax ?? 5) - (field.scaleMin ?? 1) + 1 },
                        (_, index) => (field.scaleMin ?? 1) + index
                      ).map((value) => (
                        <button
                          type="button"
                          key={value}
                          className={`formularios-scale-option ${answers[field.id || ''] === String(value) ? 'active' : ''}`}
                          onClick={() => handleScaleChange(field.id || '', value)}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                    {field.options && (field.options[0] || field.options[1]) && (
                      <div className="formularios-scale-labels" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '0 4px', boxSizing: 'border-box' }}>
                        <span style={{ fontSize: '12px', color: 'var(--tema-texto-3, #64748b)', fontWeight: 500 }}>{field.options[0] || ''}</span>
                        <span style={{ fontSize: '12px', color: 'var(--tema-texto-3, #64748b)', fontWeight: 500 }}>{field.options[1] || ''}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="formularios-modal-footer">
            <button className="formularios-btn" type="button" onClick={onClose}>Cancelar</button>
            <button className="formularios-btn primary" type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FillFormModal;
